import React from 'react';
import { Image, Source } from '@unpic/react';
import { Button, Icon } from '@printdeal/ui-library';
import { Preview as CanvasPreview, View } from '@printdeal/previews';
import type {
  GalleryAsset,
  ImageComponent,
  IProductGalleryImage,
  ProductGalleryAsset,
  ProductGalleryInteractiveImageAsset,
  ModifiedGalleryAssetsForDigitalCatalogue,
} from '../../../../types/product/productDetailPage';
import { Preview } from '../../../../types/design';
import { InteractiveBanner, InteractiveImage } from '../../../interactive-banner/InteractiveBanner';
import GalleryImage from '../GalleryImage';
import Video from '../Video';
import VideoAssetImage from '../VideoAssetImage';
import VideoThumbnailImage from '../VideoThumbnailImage';

// This shows are black outline square to indicate the design area
export const defaultDocumentReferenceUrl = 'https://uds.documents.cimpress.io/v3/documents/6284ca9a-0b04-4346-9039-5256838b8e9f/docref';

export const isInteractiveImage = (item: ProductGalleryAsset): item is ProductGalleryInteractiveImageAsset => (
  // eslint-disable-next-line no-underscore-dangle
  item.__typename === 'ContentfulInteractiveImage'
);

export const isImage = (item: ProductGalleryAsset): item is GalleryAsset => (
  // eslint-disable-next-line no-underscore-dangle
  item.__typename === 'ContentfulGalleryAsset'
);

export const isVideo = (item: ProductGalleryAsset): item is GalleryAsset => (
  // eslint-disable-next-line no-underscore-dangle
  item.__typename === 'ContentfulGalleryAsset' && (item.video !== null)
);

export const getAssetId = (video?: string) => {
  if (!video) return null;
  const url = new URL(video);
  return url.host === 'youtu.be' ? url.pathname.slice(1) : url.searchParams.get('v');
};

export const getCanvasPreviews = (
  documentReferenceUrl: string | null, views: View[],
): ImageComponent[] => (
  views.map((view) => ({
    key: view.name,
    element: (
      <div className="pd-cover-parent pd-flex pd-aspect-square pd-w-full pd-items-center pd-justify-center">
        <CanvasPreview
          view={view}
          documentReferenceUrl={documentReferenceUrl || defaultDocumentReferenceUrl}
          style={{ width: 600 }}
        />
      </div>
    ),
  })));

export const getPreviews = (previews?: Preview[], sceneFilter?: string[], className: string = ''): ImageComponent[] => previews?.filter(
  (preview) => sceneFilter?.find((filter) => preview.source.includes(filter.toLowerCase())),
).map((image) => ({
  key: image.src,
  element: <GalleryImage url={image.src} className={className} />,
})) || [];

export const getThumbnailImages = (images: IProductGalleryImage[]) =>
  images.map((image, index) => ({
    key: image.id,
    element: (
      <Image
        key={image.id}
        className="pd-cover-parent"
        layout="constrained"
        src={image.url}
        aspectRatio={1}
        alt={image.title}
        priority={index < 4}
        breakpoints={[48, 92]}
        cdn="contentful"
        width={78}
      />),
  }));

export const getGalleryImages = (images: IProductGalleryImage[]) =>
  images.map((image, index) => ({
    key: image.id,
    element: (
      <picture
        key={`gallery-img-${image.url}`}
        data-zoom={image.url}
      >
        {['avif', 'webp', null].map((format) => (
          <Source
            key={`gallery-img-${format || 'default'}`}
            type={format && `image/${format}`}
            src={image.url}
            layout="fullWidth"
            breakpoints={[150, 260, 450, 740]}
          />
        ))}
        <Image
          priority={index === 0}
          alt={image.title}
          src={image.url}
          breakpoints={[150, 260, 450, 740]}
          layout="fullWidth"
          className="pd-cover-parent"
          aspectRatio={1}
        />
      </picture>
    ),
  }));

export const getGalleryInteractiveImage = (images: InteractiveImage[]) => (images.map((image) => ({
  key: image.interactiveImageId,
  element: (
    <InteractiveBanner
      image={image}
      imageMobile={image}
    />),
})));

export const getEnlargedImages = (images: IProductGalleryImage[]) =>
  images.map((image) => ({
    key: image.id,
    element: (
      <picture key={`gallery-enlarged-img-${image.url}`}>
        {['avif', 'webp', null].map((format) => (
          <Source
            key={`gallery-enlarged-img-${format || 'default'}`}
            type={format && `image/${format}`}
            src={image.url}
            layout="fullWidth"
            breakpoints={[320, 500, 796, 1140]}
          />
        ))}
        <Image
          alt={image.title}
          src={image.url}
          breakpoints={[320, 500, 796, 1140]}
          layout="fullWidth"
          aspectRatio={1}
          className="pd-cover-parent"
        />
      </picture>),
  }));

export const getVideoElement = (video: string, assetId: string, autoPlayFlag: number) => {
  const videoId = getAssetId(video);
  if (!videoId) return [];
  return [{
    key: assetId,
    element: <Video assetId={assetId} videoId={videoId} autoPlayFlag={autoPlayFlag} />,
  }];
};

export const getVideoImageElement = (
  asset: GalleryAsset, index: number, onVideoClick: () => void,
) => {
  const videoId = isVideo(asset) && getAssetId(asset.video);
  if (!videoId) return [];
  const imageAsset = isImage(asset) ? asset.image : undefined;
  return [{
    key: asset.id,
    element: (
      <div className="pd-cover-parent pd-flex pd-items-center pd-justify-center">
        <VideoAssetImage image={imageAsset} video={asset.video} position={index} />
        <Button
          size="lg"
          className="pd-z-10"
          variant="white-circle"
          iconBefore="player-play"
          onClick={onVideoClick}
        />
      </div>
    ),
  }];
};

export const getVideoThumbnailElement = (asset: GalleryAsset, index: number) => {
  const videoId = isVideo(asset) && getAssetId(asset.video);
  if (!videoId) return [];
  const imageAsset = isImage(asset) ? asset.image : undefined;
  return [{
    key: asset.id,
    element: (
      <div className="pd-cover-parent pd-flex pd-items-center pd-justify-center after:pd-absolute after:pd-inset-0 after:pd-bg-dim-800 after:pd-opacity-20">
        <VideoThumbnailImage image={imageAsset} video={asset.video} position={index} />
        <Icon size="xl" icon="player-play-filled" className="pd-z-10 pd-text-white" />
      </div>
    ),
  }];
};

/**
   *
   * @param previewImageUrl preview image url from cloudlab
   * @param imageList image list can be thumbnails, enlarged images or images
   * @returns new updated images list with the preview image as a new item
   */
const getUpdatedImagesListWithPreview = (previewImageUrl: string, imageList: IProductGalleryImage[]):
IProductGalleryImage[] => [{
  id: btoa(`${previewImageUrl}${Date.now().toString()}`).replace(/=/g, ''),
  title: 'Product Preview',
  url: previewImageUrl,
  width: 600,
} as IProductGalleryImage, ...imageList];

/**
 *
 * @param images assets for PDP gallery images
 * @param thumbnails assets for PDP gallery thumbnails
 * @param enlargedImages assets PDP for gallery enlarged view
 * @param previewImage Image from Cloudlab / digital product catalogue page
 * @returns assets as before in the normal flow, modified assets if PDP visited from digital product catalogue
 */
export const getUpdatedGalleryAssets = (
  images: IProductGalleryImage[],
  thumbnails: IProductGalleryImage[],
  enlargedImages: IProductGalleryImage[],
  previewImage: string | null,
) :ModifiedGalleryAssetsForDigitalCatalogue => {
  if (!images && !thumbnails && !enlargedImages) {
    return {
      processedImages: [],
      processedThumbnails: [],
      processedEnlargedImages: [],
    };
  }
  if (!previewImage) {
    return {
      processedImages: images,
      processedThumbnails: thumbnails,
      processedEnlargedImages: enlargedImages,
    };
  }

  const updatedImagesList = getUpdatedImagesListWithPreview(previewImage, images);
  const updatedEnlargedImagesList = getUpdatedImagesListWithPreview(previewImage, enlargedImages);
  const updatedThumbnailsList = getUpdatedImagesListWithPreview(previewImage, thumbnails);

  return {
    processedImages: updatedImagesList,
    processedThumbnails: updatedThumbnailsList,
    processedEnlargedImages: updatedEnlargedImagesList,
  };
};
